import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { BILL_TYPE_FILTER, ETAX_CHANNEL_FILTER, STATUS_FILTER } from "./data";
import {
  getAllBranch,
  getAllRoutes,
  getAllRoutesByCompany,
} from "../../services";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { featureAccess } from "../../permissions/access";
import { SaleRoute } from "../../types/sale-route.type";
import { Branch } from "../../types/user.type";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

interface CustomerFilterProps {
  searchName: string;
  setSearchName: Dispatch<SetStateAction<string>>;
  status: string;
  setStatus: Dispatch<SetStateAction<string>>;
  billType: string;
  setBillType: Dispatch<SetStateAction<string>>;
  etaxChannel: string;
  setEtaxChannel: Dispatch<SetStateAction<string>>;
  customerCode: string;
  setCustomerCode: Dispatch<SetStateAction<string>>;
  routeCode: string;
  setRouteCode: Dispatch<SetStateAction<string>>;
  branch: string;
  setBranch: Dispatch<SetStateAction<string>>;
}

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

// The label takes only the width of its own text, so it sits next to its box instead of
// at the far side of a fixed quarter-width column. It is pinned to the height of a small
// input (40px) rather than centred on the row, so a helper text under the box
// (สายส่ง's "เลือกสาขาก่อน") no longer drags the label down.
const FilterField = ({ label, children }: FilterFieldProps) => (
  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
    <Box display="flex" alignItems="flex-start" gap={1.5}>
      <Typography
        variant="body2"
        noWrap
        sx={{ flexShrink: 0, lineHeight: "40px" }}
      >
        {label}
      </Typography>
      <Box flex={1} minWidth={0}>
        {children}
      </Box>
    </Box>
  </Grid>
);

export default function CustomerFilter({
  searchName,
  setSearchName,
  status,
  setStatus,
  billType,
  setBillType,
  etaxChannel,
  setEtaxChannel,
  customerCode,
  setCustomerCode,
  routeCode,
  setRouteCode,
  branch,
  setBranch,
}: CustomerFilterProps) {
  const role = useUserRole();
  // "May this user pick a branch", not "is this user head office" — ADMIN is not head
  // office, but it maintains master data for shops in every branch and has to be able to
  // reach them. The name carries that, so the route dropdown below reads correctly too:
  // whoever can pick a branch must read that branch's routes rather than their own.
  const canPickBranch = featureAccess.customerBranchScope(role);

  const [routes, setRoutes] = useState<SaleRoute[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);

  const onGetAllBranch = useCallback(async () => {
    if (!canPickBranch) return;
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data);
  }, [canPickBranch]);

  useEffect(() => {
    onGetAllBranch();
  }, [onGetAllBranch]);

  const getRoutes = useCallback(async () => {
    if (canPickBranch) {
      if (!branch || branch === "all") {
        setRoutes([]);
        return;
      }
      const companyId = branches.find((b) => b.uuid === branch)?.company_id;
      if (!companyId) {
        setRoutes([]);
        return;
      }
      const {
        data: { data },
      } = await getAllRoutes(companyId);
      setRoutes(Array.isArray(data) ? data : data.rows ?? []);
    } else {
      const {
        data: { data },
      } = await getAllRoutesByCompany();
      setRoutes(data);
    }
  }, [canPickBranch, branch, branches]);

  useEffect(() => {
    getRoutes();
  }, [getRoutes]);

  const isRouteDisabled = canPickBranch && (!branch || branch === "all");

  // Four fields to a row: the text searches and the two list-wide selects first, then
  // where the shop is (สาขา → สายส่ง) and which tax invoice it gets.
  return (
    <Grid container columnSpacing={3} rowSpacing={2} mb={2}>
      <FilterField label="ชื่อลูกค้า">
        <TextField
          size="small"
          fullWidth
          placeholder="ค้นหาชื่อลูกค้า"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />
      </FilterField>
      <FilterField label="รหัสลูกค้า">
        <TextField
          size="small"
          fullWidth
          placeholder="กรอกรหัสลูกค้า"
          value={customerCode}
          onChange={(e) => setCustomerCode(e.target.value)}
        />
      </FilterField>
      <FilterField label="สถานะ">
        <TextField
          select
          size="small"
          fullWidth
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {STATUS_FILTER.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="ประเภทบิล">
        <TextField
          select
          size="small"
          fullWidth
          value={billType}
          onChange={(e) => setBillType(e.target.value)}
        >
          {BILL_TYPE_FILTER.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      {canPickBranch && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={branch}
            onChange={(e) => {
              setBranch(e.target.value);
              setRouteCode("all");
            }}
          >
            <MenuItem value="all">ทั้งหมด</MenuItem>
            {branches.map((b) => (
              <MenuItem key={b.uuid} value={b.uuid}>
                {b.branch_code} - {b.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}
      <FilterField label="สายส่ง">
        <TextField
          select
          size="small"
          fullWidth
          disabled={isRouteDisabled}
          value={routeCode}
          onChange={(e) => setRouteCode(e.target.value)}
          helperText={isRouteDisabled ? "เลือกสาขาก่อน" : undefined}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {routes.map((route) => (
            <MenuItem key={route.route_code} value={route.route_code}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="ใบกำกับภาษี">
        <TextField
          select
          size="small"
          fullWidth
          value={etaxChannel}
          onChange={(e) => setEtaxChannel(e.target.value)}
        >
          {ETAX_CHANNEL_FILTER.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
    </Grid>
  );
}
